import logger from '../../log';
import autobind from '../../autobind';
import { Node, ConnectionState } from './interfaces';
import { Subject, Observable } from 'rxjs';
import { RTCSignalOps } from './SignalBus';
import makeRTCPeerConnection from './makeRTCPeerConnection';
import { WebrtcConfig } from '../../config/WebrtcConfig';
import { AgentCodes } from './AgentCodes';
import processIceCandidates from '../processIceCandidates';
import { getElevenData } from '../../ecdn11/store11AgentInfo';
import { ElevenAgentData } from '../../ecdn11/interfaces';

const log = logger.getLogger(__filename);

export class ChannelError extends Error {
  cause: Error | undefined;
  statusCode: number;
  constructor(message: string, statusCode: number, cause?: Error) {
    super(message);
    this.statusCode = statusCode;
    this.cause = cause;
  }
}

function initializePeerConnection(channel: PeerChannel): RTCPeerConnection {
  const pc = makeRTCPeerConnection(
    channel.rtcConfiguration,
    channel.constraints
  );

  pc.oniceconnectionstatechange = () => {
    // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/iceConnectionState
    switch (pc.iceConnectionState) {
      case 'new':
        // initializing
        break;
      case 'checking':
        // checking connectivity
        break;
      case 'connected':
        // tentative connection
        // ie it's negotiating
        break;
      case 'completed':
        // full connection
        break;
      case 'disconnected':
        // lost connectivity - could close or stabilize
        // channel may not have failed but is in an unstable state. It can return to connected.
        // In the future we may want to provide a signal that the peer is "unavailable" or not ready.
        break;
      // TODO - for failed or closed - do we try ice restart?
      case 'failed':
        // terminated connection
        log.warn(`Ice connection state has failed`, channel.peer);
        break;
      case 'closed':
        // terminated connection
        break;
      default:
        break;
    }
  };

  pc.onicecandidate = (evt: RTCPeerConnectionIceEvent) => {
    const { candidate } = evt;
    log.debug('onicecandidate candidate:', candidate);

    if (candidate) {
      channel.localCandidates.push(candidate);
    } else {
      const elevenData = getElevenData();

      const toSend = processIceCandidates(
        channel.localCandidates,
        (elevenData as ElevenAgentData).interfaces
      );
      log.info('Modified candidates:', toSend);
      toSend.forEach(c => channel.sendIceCandidate(c));
      channel.candidatesComplete = true;
    }
  };

  // The below link contains a diagram of the state machine
  // https://w3c.github.io/webrtc-pc/archives/20151006/images/peerstates.svg
  pc.onsignalingstatechange = event => {
    // https:developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/signalingState
    log.info(`signaling state ${pc.signalingState}`, event);
    switch (pc.signalingState) {
      case 'stable':
        // if we have transitioned from off or answer to stable, we know that the signaling state is complete.
        if (channel.hasOffer || channel.hasAnswer) {
          channel.signalingStable = true;
          log.debug(`signaling STABLE ${pc.signalingState}`);
          channel.addPendingCandidates();
        }
        break;
      case 'have-local-offer':
      case 'have-remote-offer':
        // We have an offer whether remote or local
        log.info('have offer');
        channel.hasOffer = true;
        break;
      case 'have-local-pranswer':
      case 'have-remote-pranswer':
        // we have an answer whether remote or local.
        log.info('have answer');
        channel.hasAnswer = true;
        break;
    }
  };

  pc.onconnectionstatechange = () => {
    // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/connectionState
    // https://developer.mozilla.org/en-US/docs/Web/API/RTCPeerConnection/onconnectionstatechange
    switch (pc.connectionState) {
      case 'connected':
        // The connection has become fully connected
        break;
      case 'disconnected':
        break;
      case 'failed':
        // One or more transports has terminated unexpectedly or in an error
        log.info(
          `Failing connection because connectionState transitioned to ${pc.connectionState}`,
          channel.node,
          channel.peer
        );
        channel.close(
          ConnectionState.FAILED,
          new ChannelError(
            'Failed state transition in RTC connection',
            AgentCodes.DATACHANNEL_FAILED_STATE_TRANSITION
          )
        );
        break;
      case 'closed':
        // The connection has been closed
        log.info(
          `Connection state transitioned to ${pc.connectionState}`,
          channel.node,
          channel.peer
        );
        channel.close(
          ConnectionState.CLOSED,
          new ChannelError(
            `Connection state transitioned to ${pc.connectionState}`,
            503
          )
        );
        break;
      default:
        break;
    }
  };

  return pc;
}

function getData(event: MessageEvent): Promise<ArrayBuffer> {
  const { data } = event;
  // Most browsers return ArrayBuffers as messages.
  if (data instanceof ArrayBuffer) {
    return Promise.resolve(data);
  } else if (typeof data.arrayBuffer === 'function') {
    // Firefox returns a Blob, which has a method `arrayBuffer` that returns a promise with the data as an arrayBuffer
    // https://developer.mozilla.org/en-US/docs/Web/API/Blob
    return data.arrayBuffer();
  } else {
    // No idea what this is, so return it. Majority of cases should run through the above 2 checks.
    return Promise.resolve(data);
  }
}

interface ChannelEvent {
  target: PeerChannel;
  state: ConnectionState;
  error?: ChannelError;
}

export interface RTCConstraints {
  optional: [{ googDscp: true }];
}

const GOOGLE_RTC_DSCP_OPTIONS: RTCConstraints = {
  optional: [{ googDscp: true }]
};

export function getConstraints(
  webrtc: WebrtcConfig
): RTCConstraints | undefined {
  if (webrtc.enableDscpTagging) {
    return GOOGLE_RTC_DSCP_OPTIONS;
  } else {
    return undefined;
  }
}

export default class PeerChannel {
  private retryCount = 0;
  private maxRetries = 3;

  node: Node;
  peer: Node;
  rtcConfiguration: RTCConfiguration;
  constraints?: RTCConstraints;
  connectionOffered: boolean;
  localCandidates: RTCIceCandidate[];
  remoteCandidates: RTCIceCandidateInit[];
  signalConnection: RTCSignalOps;
  peerConnection: RTCPeerConnection;
  dataChannel?: RTCDataChannel;
  channelOptions?: RTCDataChannelInit;

  channel: Subject<ArrayBuffer>;
  events: Subject<ChannelEvent>;

  // TODO
  errorHandler?: (msg: string, code: number) => void;
  hasOffer = false;
  hasAnswer = false;
  signalingStable = false;
  candidatesComplete = false;
  state: ConnectionState = ConnectionState.PENDING;

  constructor(opts: {
    node: Node;
    peer: Node;
    rtcConfiguration: RTCConfiguration;
    signalConnection: RTCSignalOps;
    constraints?: RTCConstraints;
    offer?: RTCSessionDescriptionInit;
    startupTimeout?: number;
  }) {
    autobind(this);
    this.node = opts.node;
    this.peer = opts.peer;
    this.rtcConfiguration = opts.rtcConfiguration;
    this.constraints = opts.constraints;
    this.connectionOffered = false;
    this.signalConnection = opts.signalConnection;
    this.localCandidates = [];
    this.remoteCandidates = [];
    this.channel = new Subject();
    this.events = new Subject();
    this.peerConnection = initializePeerConnection(this);
    if (opts.offer) {
      this.handleOffer(opts.offer);
    } else {
      this.makeOffer();
    }

    setTimeout(() => {
      if (this.state != ConnectionState.ESTABLISHED) {
        log.warn(
          `Timed out while trying to connect with peer ${this.peer.nodeId}`,
          this.peerConnection
        );
        this.close(
          ConnectionState.FAILED,
          new ChannelError(
            'Connection establishment timed out',
            AgentCodes.DATACHANNEL_TIMED_OUT
          )
        );
      }
    }, opts.startupTimeout || 10000);
  }

  private setupDataChannel() {
    if (this.dataChannel) {
      this.dataChannel.binaryType = 'arraybuffer';
      this.dataChannel.onopen = () => {
        this.setState(ConnectionState.ESTABLISHED);
      };
      this.dataChannel.onerror = e => {
        log.info('data channel error', e, this);
        this.close(
          ConnectionState.FAILED,
          new ChannelError(
            'DataChannelClosed',
            AgentCodes.DATACHANNEL_CLOSED,
            (e as unknown as Record<string, Error>).error
          )
        );
      };
      this.dataChannel.onclose = e => {
        log.info('data channel closed', e, this);
        this.close(
          ConnectionState.CLOSED,
          new ChannelError('DataChannelClosed', AgentCodes.DATACHANNEL_CLOSED)
        );
      };
      this.dataChannel.onmessage = async e => {
        const data = await getData(e);
        this.channel.next(data);
      };
    } else {
      log.error("Cannot setup datachannel when it's not created");
    }
  }

  handleOffer(
    offer: RTCSessionDescriptionInit,
    iceRestart = false
  ): Promise<void> {
    this.hasOffer = false;
    if (iceRestart) {
      this.peerConnection.close();
      this.dataChannel && this.dataChannel.close();
      this.peerConnection = initializePeerConnection(this);
    }
    // answer
    this.peerConnection.ondatachannel = e => {
      this.dataChannel = e.channel;
      this.setupDataChannel();
    };
    return this.peerConnection
      .setRemoteDescription(offer)
      .then(() => {
        this.peerConnection.createAnswer().then(
          answer => {
            this.peerConnection.setLocalDescription(answer);
            // local becomes remote and vice versa
            this.signalConnection.provideAnswer(this.peer, this.node, answer);
            return;
          },
          error => {
            this.onerror(
              `Failed to create an answer to remote peer. restart: ${iceRestart}`,
              AgentCodes.RTC_SDP_ANSWER_CREATE_ERROR,
              [error]
            );
          }
        );
      })
      .catch(error => {
        // if the offer is not null, then extract the fields appropriately so the it doesn't have circular reference errors when stringifying. Otherwise, send the "nullish" value.
        const description = offer
          ? JSON.stringify({
              type: offer.type,
              sdp: offer.sdp
            })
          : offer;
        this.onerror(
          `Failed to set the remote description. restart: ${iceRestart}, description: ${description}, signalingState: ${this.peerConnection.signalingState}.`,
          AgentCodes.RTC_SDP_REMOTE_DESCRIPTION_ERROR,
          [error]
        );
      });
  }

  private makeOffer(iceRestart = false) {
    this.connectionOffered = true;
    this.hasAnswer = false;
    if (iceRestart) {
      this.retryCount = this.retryCount + 1;
      this.peerConnection.close();
      this.dataChannel && this.dataChannel.close();
      this.peerConnection = initializePeerConnection(this);
    }
    this.dataChannel = this.peerConnection.createDataChannel(
      this.node.nodeId,
      this.channelOptions
    );
    this.setupDataChannel();
    this.peerConnection.onnegotiationneeded = async () => {
      log.info('Negotiation of the connection is needed', this.peerConnection);
      if (this.peerConnection.signalingState === 'closed') {
        log.warn('Trying to negotiate on a closed connection');
        this.close(
          ConnectionState.FAILED,
          new ChannelError(
            'Attempting to negotiate closed peer connection',
            AgentCodes.DATACHANNEL_NEGOTIATION_ON_CLOSED_CHANNEL
          )
        );
        return;
      }
      this.peerConnection
        .createOffer({ iceRestart })
        .then(o => this.peerConnection.setLocalDescription(o))
        .then(() => {
          const local = this.peerConnection.localDescription;
          if (!local) {
            this.close(
              ConnectionState.FAILED,
              new ChannelError(
                `Failed to create a local description`,
                AgentCodes.DATACHANNEL_FAILED_LOCAL_DESCRIPTION
              )
            );
          } else {
            this.signalConnection.provideOffer(this.peer, this.node, local);
          }
        });
    };
  }

  send(data: ArrayBuffer): number {
    if (this.isClosed()) {
      log.info('Peer connection is shutdown, cannot send message');
      return AgentCodes.DATACHANNEL_CLOSED;
    }

    try {
      if (this.dataChannel && this.dataChannel.readyState === 'open') {
        log.debug(
          `Sending to datachannel ${this.dataChannel.label} with buffered amount ${this.dataChannel.bufferedAmount} and buffered amount threshold ${this.dataChannel.bufferedAmountLowThreshold}`
        );
        this.dataChannel.send(data);
        return AgentCodes.STATUS_OK;
      } else {
        log.info(
          'DataChannel is not in a ready state',
          this.dataChannel && this.dataChannel.readyState,
          data
        );
        return AgentCodes.DATACHANNEL_UNINITIALIZED;
      }
    } catch (err) {
      if (this.isClosed()) {
        log.info('Connection is close, message cannot be sent');
      }
      log.warn(
        `Failed to send on data channel to peer: ${this.peer.nodeId}`,
        err
      );
      if ((err as Record<string, string>).name) {
        switch ((err as Record<string, string>).name) {
          case 'InvalidStateError':
            // Underlying cause: readyState is connecting, nothing is sent
            return AgentCodes.DATACHANNEL_NOT_READY;
          case 'NetworkError':
            // Underlying cause: Specified data would need to be buffered and
            // there isn't room for it in the buffer.
            // NOTE: transport is immediately closed.
            return AgentCodes.DATACHANNEL_BUFFER_FULL;
          case 'TypeError':
            // Underlying cause: data too large for other peer to receive
            return AgentCodes.PAYLOAD_TOO_LARGE;
        }
      }
      // Underlying cause: An unknown error from the data channel
      return AgentCodes.DATACHANNEL_UNKNOWN_ERROR;
    }
  }

  listenToMessages(): Observable<ArrayBuffer> {
    return this.channel.asObservable();
  }

  isClosed(): boolean {
    return (
      this.state === ConnectionState.CLOSED ||
      this.state === ConnectionState.FAILED
    );
  }

  sendIceCandidate(candidate: RTCIceCandidate): void {
    this.signalConnection.sendIceCandidate(this.peer, this.node, candidate);
  }

  addPendingCandidates(): void {
    this.remoteCandidates.forEach(candidate => {
      if (this.peerConnection) {
        this.peerConnection.addIceCandidate(candidate);
      }
    });
  }

  addRemoteCandidate(candidate: RTCIceCandidateInit): void {
    try {
      if (this.peerConnection) {
        this.remoteCandidates.push(candidate);
        if (this.signalingStable) {
          // when signalling has moved to stable we can add the candidate.
          this.peerConnection.addIceCandidate(candidate);
        }
      } else {
        log.error(
          'Received iceCandidate when peerConnection was null.',
          candidate,
          this.peer
        );
      }
    } catch (err) {
      log.error('failed to add ice candidate', err, this.peer);
    }
  }

  setRemoteAnswer(desc: RTCSessionDescriptionInit): Promise<void> {
    // this won't work
    if (desc.type !== 'answer') {
      log.warn(
        `${this.node.nodeId} from ${this.peer.nodeId} received a description that is not of type answer`
      );
    }
    if (
      this.peerConnection.signalingState === 'stable' ||
      this.peerConnection.remoteDescription
    ) {
      log.warn(
        `${this.node.nodeId} from ${this.peer.nodeId} received an answer when a remote description already exists.`
      );
      return Promise.resolve();
    }

    return this.peerConnection.setRemoteDescription(desc);
  }

  // TODO - Do we need this?
  onerror(errorMsg: string, errorCode: number, errorObjs: Error[] = []): void {
    log.warn('Connection error: ' + errorMsg, errorObjs, 'peer: ', this.peer);
    if (this.errorHandler) {
      this.errorHandler(errorMsg, errorCode);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _shouldRetry(state: ConnectionState, _error?: Error): boolean {
    if (!this.connectionOffered) {
      return false;
    }
    if (this.retryCount >= this.maxRetries) {
      log.error('DataChannel retry count exceeded with peer: ', this.peer);
      return false;
    }
    switch (state) {
      case ConnectionState.CLOSED:
      case ConnectionState.FAILED:
        return true;
      default:
        return false;
    }
  }

  _retry(previous: ConnectionState): void {
    // offer connection
    this.retryCount = this.retryCount + 1;
    this.state = ConnectionState.DISCONNECTED;
    log.warn(`Retrying connection with previous state: ${previous}`);
    log.warn(`Previous RTC Connection states`, this.getStats());

    this.makeOffer(true);
  }

  setState(state: ConnectionState, error?: ChannelError): boolean {
    const previous = this.state;
    this.state = state;
    if (this._shouldRetry(state, error)) {
      this._retry(previous);
      return false;
    } else {
      this.events.next({ target: this, state, error });
      return true;
    }
  }

  async getStats(): Promise<RTCStatsReport> {
    const stats = await this.peerConnection.getStats();
    return stats;
  }

  listenToEvents(): Observable<ChannelEvent> {
    return this.events.asObservable();
  }

  private cleanup(): void {
    if (this.dataChannel) {
      this.dataChannel.close();
    }
    if (this.peerConnection) {
      this.peerConnection.close();
    }
  }

  close(
    state: ConnectionState = ConnectionState.CLOSED,
    error?: ChannelError
  ): void {
    if (this.isClosed()) {
      log.warn('Channel already closed', error);
      return;
    }
    if (this.setState(state, error)) {
      this.cleanup();
      this.channel.complete();
      this.events.complete();
    }
  }
}
